import type { RunStats } from '@/game/types'
import { readJson, remove, writeJson } from './storage'

export interface ScoreEntry {
  name: string
  score: number
  /** epoch ms */
  ts: number
  code: string
  stats: RunStats
}

const KEY = 'board.v1'
/** Hard cap on persisted runs — a fair day produces a few hundred at most. */
const MAX_ENTRIES = 200
const DEFAULT_LIMIT = 10

function load(): ScoreEntry[] {
  const raw = readJson<ScoreEntry[]>(KEY, [])
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (e): e is ScoreEntry =>
      !!e && typeof e.name === 'string' && typeof e.score === 'number' && typeof e.ts === 'number',
  )
}

/** Score desc, then earlier run first — the first person to reach a score keeps the higher rank. */
function byRank(a: ScoreEntry, b: ScoreEntry): number {
  return b.score - a.score || a.ts - b.ts
}

function isToday(ts: number, now: number): boolean {
  const day = new Date(ts)
  const today = new Date(now)
  return (
    day.getFullYear() === today.getFullYear() &&
    day.getMonth() === today.getMonth() &&
    day.getDate() === today.getDate()
  )
}

/** Persists the entry and returns its 1-based rank in today's board. */
export function saveScore(entry: ScoreEntry): number {
  const all = load()
  all.push(entry)
  all.sort(byRank)
  writeJson(KEY, all.slice(0, MAX_ENTRIES))

  const now = Date.now()
  const today = all.filter((e) => isToday(e.ts, now))
  const index = today.findIndex((e) => e.code === entry.code && e.ts === entry.ts)
  return index < 0 ? today.length + 1 : index + 1
}

export function topToday(limit = DEFAULT_LIMIT): ScoreEntry[] {
  const now = Date.now()
  return load()
    .filter((e) => isToday(e.ts, now))
    .sort(byRank)
    .slice(0, limit)
}

export function topAllTime(limit = DEFAULT_LIMIT): ScoreEntry[] {
  return load().sort(byRank).slice(0, limit)
}

/** Best score today for that name, or null. */
export function personalBestToday(name: string): number | null {
  const wanted = name.trim().toLowerCase()
  if (!wanted) return null
  const now = Date.now()
  let best: number | null = null
  for (const e of load()) {
    if (!isToday(e.ts, now) || e.name.trim().toLowerCase() !== wanted) continue
    if (best === null || e.score > best) best = e.score
  }
  return best
}

export function clearBoard(): void {
  remove(KEY)
}
